import Hero from "@/components/Hero";
import AboutManifesto from "@/components/AboutManifesto";
import FeaturedCars from "@/components/FeaturedCars";
import ReviewsSection from "@/components/ReviewsSection";
import SoldStrip from "@/components/SoldStrip";
import ContactSection from "@/components/ContactSection";
import { GOOGLE_REVIEWS_URL } from "@/components/GoogleReviewsCTA";
import { Star, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/** Logo Google multicolore inline — nessuna dipendenza esterna */
function GoogleGlyph({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

/** CTA finale — grande, centrata, come ultimo blocco prima del footer */
function GoogleReviewsFinal() {
  const { lang } = useLang();
  return (
    <section
      className="relative py-32 sm:py-40 px-6 sm:px-10 lg:px-14 border-t border-white/10"
      data-testid="home-google-reviews-final"
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="label-eyebrow text-white/60 mb-6">
          {lang === "en" ? "Verified on Google Business" : "Verificato su Google Business"}
        </div>

        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white mb-6">
          {lang === "en" ? "Your opinion" : "La tua opinione"}<br />
          <span className="italic text-neutral-500">{lang === "en" ? "makes the difference." : "fa la differenza."}</span>
        </h2>

        <div className="flex items-center justify-center gap-1.5 mb-10" aria-label="5 su 5 stelle">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={22} className="fill-[#F4B400] text-[#F4B400]" strokeWidth={1.2} />
          ))}
        </div>

        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-4 bg-white text-black hover:bg-neutral-100 border border-white px-7 sm:px-9 py-5 transition-colors shadow-[0_20px_40px_-15px_rgba(255,255,255,0.15)]"
          data-testid="google-review-final-btn"
        >
          <GoogleGlyph size={30} />
          <span className="font-medium text-lg sm:text-xl tracking-tight">
            {lang === "en" ? "Leave a review on Google" : "Lascia una recensione su Google"}
          </span>
          <ExternalLink size={18} className="text-neutral-500 group-hover:translate-x-1 group-hover:text-black transition-all" />
        </a>

        <p className="mt-8 text-sm text-neutral-500 max-w-xl mx-auto leading-relaxed">
          {lang === "en"
            ? "Your review helps other enthusiasts choose with confidence. Thank you for supporting our work."
            : "La tua recensione aiuta altri appassionati a scegliere con fiducia. Grazie per sostenere il nostro lavoro."}
        </p>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <AboutManifesto />
      <FeaturedCars />
      <ReviewsSection />
      <SoldStrip />
      <ContactSection />
      <GoogleReviewsFinal />
    </div>
  );
}
